import React, { useState, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import CompletionRuleEvaluator from '../../services/CompletionRuleEvaluator';
import { supabase } from '../../lib/supabase';

const CompletionRuleAdmin = () => {
  const { familyUsers } = useFamilyContext();
  const [evaluating, setEvaluating] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [logStats, setLogStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const children = familyUsers?.filter(user => user.type !== 'parent') || [];

  // Load completion log stats on mount
  useEffect(() => {
    if (selectedUserId) {
      loadLogStats(selectedUserId);
    }
  }, [selectedUserId]);

  const loadLogStats = async (userId) => {
    setLoading(true);
    try {
      // Get stats from completion_log table
      const { data, error } = await supabase
        .from('completion_log')
        .select('completion_type, completion_system, created_at')
        .eq('user_id', userId)
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()); // Last 30 days

      if (error) throw error;

      // Calculate stats
      const stats = {
        total: data?.length || 0,
        autoCompleted: data?.filter(log => log.completion_type === 'auto_complete').length || 0,
        autoIncompleted: data?.filter(log => log.completion_type === 'auto_incomplete').length || 0,
        bySystem: {
          completion_rule: data?.filter(log => log.completion_system === 'completion_rule').length || 0,
          workflow_trigger: data?.filter(log => log.completion_system === 'workflow_trigger').length || 0
        }
      };

      setLogStats(stats);
    } catch (error) {
      console.error('Error loading log stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluateRules = async (userId) => {
    if (!userId) {
      alert('Please select a student to evaluate');
      return;
    }

    setEvaluating(true);
    setResults(null);

    try {
      console.log('🔄 Manually triggering completion rule evaluation for user:', userId);
      const evaluationResults = await CompletionRuleEvaluator.evaluateUserTasks(userId);
      
      setResults(evaluationResults);
      
      const selectedUser = children.find(child => child.id === userId);
      const userName = selectedUser?.name || selectedUser?.displayName || 'Unknown';
      
      if (evaluationResults.completed > 0 || evaluationResults.incompleted > 0) {
        alert(`Evaluation complete for ${userName}!\n\nAuto-completed: ${evaluationResults.completed} tasks\nAuto-incompleted: ${evaluationResults.incompleted} tasks`);
      } else {
        alert(`Evaluation complete for ${userName}!\n\nNo tasks needed completion status changes.`);
      }
      
    } catch (error) {
      console.error('Error evaluating completion rules:', error);
      alert('Error evaluating completion rules: ' + error.message);
    } finally {
      setEvaluating(false);
    }
  };

  const handleEvaluateAllUsers = async () => {
    if (children.length === 0) {
      alert('No students found');
      return;
    }

    setEvaluating(true);
    setResults(null);

    try {
      let totalCompleted = 0;
      let totalIncompleted = 0;
      const allResults = [];

      for (const child of children) {
        console.log(`🔄 Evaluating completion rules for ${child.name || child.displayName}`);
        const userResults = await CompletionRuleEvaluator.evaluateUserTasks(child.id);
        
        totalCompleted += userResults.completed || 0;
        totalIncompleted += userResults.incompleted || 0;
        
        allResults.push({
          user: child.name || child.displayName,
          ...userResults
        });
      }

      setResults({
        completed: totalCompleted,
        incompleted: totalIncompleted,
        allResults
      });

      alert(`Evaluation complete for all students!\n\nTotal auto-completed: ${totalCompleted} tasks\nTotal auto-incompleted: ${totalIncompleted} tasks`);
      
    } catch (error) {
      console.error('Error evaluating completion rules for all users:', error);
      alert('Error evaluating completion rules: ' + error.message);
    } finally {
      setEvaluating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-azure-50 border border-azure-200 rounded-lg p-4 text-sm">
        <div className="font-semibold mb-2 text-azure-800">How Completion Rules Work</div>
        <ul className="text-azure-700 space-y-1 text-xs">
          <li>• <strong>OLD System (completion_rule):</strong> Profile tasks, school selection tasks</li>
          <li>• <strong>NEW System (workflow_trigger):</strong> Essay workflows, recommendation workflows, application workflows</li>
          <li>• Both systems now log to <code>completion_log</code> table for unified tracking</li>
        </ul>
        <div className="mt-3 pt-3 border-t border-azure-300">
          <div className="text-xs text-azure-600">
            <div className="font-semibold mb-1">Examples:</div>
            <div>📋 OLD: "Finalize Reach Schools" (requires locking)</div>
            <div>🔄 NEW: Essay parent tasks (auto-complete when all subtasks done)</div>
          </div>
        </div>
      </div>

      {/* Completion Log Stats */}
      {selectedUserId && logStats && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="font-semibold mb-2 text-emerald-800">Recent Completion Activity (Last 30 Days)</div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs text-emerald-600 mb-1">Total Events</div>
              <div className="text-2xl font-bold text-emerald-900">{logStats.total}</div>
            </div>
            <div>
              <div className="text-xs text-emerald-600 mb-1">Auto-Completed</div>
              <div className="text-2xl font-bold text-emerald-900">{logStats.autoCompleted}</div>
            </div>
            <div>
              <div className="text-xs text-emerald-600 mb-1">OLD System</div>
              <div className="text-lg font-semibold text-emerald-800">{logStats.bySystem.completion_rule}</div>
            </div>
            <div>
              <div className="text-xs text-emerald-600 mb-1">NEW System (Workflows)</div>
              <div className="text-lg font-semibold text-emerald-800">{logStats.bySystem.workflow_trigger}</div>
            </div>
          </div>
        </div>
      )}

      {/* Individual Student Selection */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Evaluate Rules for Specific Student
          </label>
          <div className="flex gap-2">
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="flex-1 border border-stone-300 rounded-md px-3 py-2 text-sm"
              disabled={evaluating}
            >
              <option value="">Select a student...</option>
              {children.map(child => (
                <option key={child.id} value={child.id}>
                  {child.name || child.displayName}
                </option>
              ))}
            </select>
            <button
              onClick={() => handleEvaluateRules(selectedUserId)}
              disabled={evaluating || !selectedUserId}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                evaluating || !selectedUserId
                  ? 'bg-stone-300 text-stone-500 cursor-not-allowed'
                  : 'bg-azure-600 text-white hover:bg-azure-700'
              }`}
            >
              {evaluating ? 'Evaluating...' : 'Evaluate Rules'}
            </button>
          </div>
        </div>

        {/* Evaluate All Students */}
        <div className="pt-2 border-t border-stone-200">
          <button
            onClick={handleEvaluateAllUsers}
            disabled={evaluating || children.length === 0}
            className={`w-full px-4 py-2 rounded text-sm font-medium transition-colors ${
              evaluating || children.length === 0
                ? 'bg-stone-300 text-stone-500 cursor-not-allowed'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            }`}
          >
            {evaluating ? 'Evaluating All Students...' : `Evaluate Rules for All Students (${children.length})`}
          </button>
        </div>
      </div>

      {/* Results Display */}
      {results && (
        <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
          <div className="font-semibold mb-2 text-slate-800">Latest Evaluation Results</div>
          <div className="text-sm text-slate-600">
            <div>Auto-completed: <span className="font-medium text-emerald-600">{results.completed || 0}</span> tasks</div>
            <div>Auto-incompleted: <span className="font-medium text-orange-600">{results.incompleted || 0}</span> tasks</div>
            {results.errors && results.errors.length > 0 && (
              <div className="mt-2">
                <div className="font-medium text-red-600">Errors:</div>
                <ul className="list-disc list-inside text-xs text-red-500">
                  {results.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="mt-3 pt-3 border-t border-stone-300">
            <div className="text-xs text-stone-500">
              ℹ️ This shows results from the OLD completion_rule system only. See "Recent Completion Activity" above for combined tracking.
            </div>
          </div>
        </div>
      )}

      {/* Current Status */}
      <div className="text-xs text-stone-500">
        <div>💡 <strong>Pro Tip:</strong> Both OLD and NEW completion systems now log to <code>completion_log</code> table.</div>
        <div>🔧 Use "Evaluate Rules" button to manually trigger OLD system evaluation.</div>
        <div>📊 NEW workflow completions happen automatically via database triggers.</div>
      </div>
    </div>
  );
};

export default CompletionRuleAdmin;